import { router } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';

import { IconAdd, IconDelete, IconRemove, Text, tokens } from '@fhs/ui';
import { useCartItemEditingTools } from '@fhs-legacy/frontend/src/hooks/use-cart-item-editing-tools';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { ADD_TO_CART, DELETE_ITEM, REMOVE_ITEM } from '@fhs-legacy/frontend/src/utils/test-ids';
import { ICartEntry } from '@rbi-ctg/menu';

enum IncrementorActions {
  Increment = 'increment',
  Decrement = 'decrement',
}

export default function EditQuantityToggle({ item }: { item: ICartEntry }) {
  const offerEligibleItem = useAppSelector(state =>
    selectors.loyalty.selectOfferEligibleItem(state, item.cartId)
  );
  const { handleQuantityChange } = useCartItemEditingTools({ item });

  if (offerEligibleItem) {
    return (
      <Pressable
        testID={DELETE_ITEM}
        style={[styles.quantityButton, styles.quantityContainer, styles.debugHitSlop]}
        onPress={() => router.setParams({ action: 'remove-item', id: item.cartId })}
        accessibilityRole="button"
        accessibilityLabel="delete item"
        accessibilityHint="Remove this item from your cart"
        hitSlop={{ top: 50, bottom: 50, left: 50, right: 50 }}
      >
        <IconDelete size={21.6} />
      </Pressable>
    );
  }

  return (
    <View testID={`cart-item-${item.cartId || item._id}`} style={styles.quantityContainer}>
      <Pressable
        testID={REMOVE_ITEM}
        style={[styles.quantityButton, styles.debugHitSlop]}
        onPress={() => {
          if (item.quantity === 1) {
            router.setParams({ action: 'remove-item', id: item.cartId });
          } else {
            handleQuantityChange(item.quantity - 1, IncrementorActions.Decrement);
          }
        }}
        accessibilityRole="button"
        accessibilityLabel={item.quantity === 1 ? 'Remove item from cart' : 'Remove one item'}
        accessibilityHint={
          item.quantity === 1
            ? 'Removes this item from your cart'
            : `Decreases quantity to ${item.quantity - 1}`
        }
        hitSlop={{ top: 50, bottom: 50, left: 50, right: 5 }}
      >
        {item.quantity === 1 ? <IconDelete size={21.6} /> : <IconRemove size={21.6} />}
      </Pressable>
      <Text.Ui
        weight="bold"
        size="md"
        style={{ width: 27, textAlign: 'center' }}
        accessibilityRole="text"
        accessibilityLabel={`Quantity: ${item.quantity}`}
      >
        {item.quantity}
      </Text.Ui>

      <Pressable
        testID={ADD_TO_CART}
        style={[styles.quantityButton, styles.debugHitSlop]}
        onPress={() => handleQuantityChange(item.quantity + 1, IncrementorActions.Increment)}
        disabled={item.quantity >= 10}
        accessibilityRole="button"
        accessibilityLabel="Add one item"
        accessibilityHint={
          item.quantity === 9
            ? 'Maximum quantity of 9 reached'
            : `Increases quantity to ${item.quantity + 1}`
        }
        accessibilityState={{ disabled: item.quantity === 9 }}
        hitSlop={{ top: 50, bottom: 50, left: 5, right: 50 }}
      >
        <IconAdd
          color={item.quantity < 10 ? undefined : tokens.colors.$blackOpacity04}
          size={21.6}
        />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  quantityContainer: {
    backgroundColor: tokens.colors.$blackOpacity04,
    borderRadius: 24.3,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    height: 33.75,
    width: 113.4,
  },

  quantityButton: {
    padding: 12,
  },

  debugHitSlop: {
    borderRadius: 50,
  },
});
